import {
	createSelector,
	createSlice,
	type PayloadAction
} from '@reduxjs/toolkit'
import { createSetter } from 'shared/redux-helpers'

import type { State } from './types'

import { api } from './api'

interface SliceState {
	enabledFilters: State[]

	isUpdatingTable: boolean
	scrollToAction: boolean
	searchValue: string
}

const initialState: SliceState = {
	enabledFilters: [],
	isUpdatingTable: false,
	scrollToAction: false,
	searchValue: ''
}

function changeLoading(state: SliceState, newValue = false) {
	state.isUpdatingTable = newValue
}

function enableLoading(state: SliceState) {
	changeLoading(state, true)
}

function resetLoading(state: SliceState) {
	changeLoading(state, false)
}

const checkingLoaderEndpoints = [
	api.endpoints.archiveCampaign,
	api.endpoints.campaignsPage,
	api.endpoints.cloneCampaign,
	api.endpoints.completeCampaign,
	api.endpoints.runCampaign,
	api.endpoints.stopCampaign,
	api.endpoints.unArchiveCampaign
]

const campaignsSlice = createSlice({
	extraReducers: builder => {
		for (const endpoint of checkingLoaderEndpoints) {
			builder.addMatcher(endpoint.matchPending, enableLoading)
			builder.addMatcher(endpoint.matchFulfilled, resetLoading)
			builder.addMatcher(endpoint.matchRejected, resetLoading)
		}
	},
	initialState,
	name: 'campaigns',
	reducers: {
		setScrollToAction: createSetter<SliceState>('scrollToAction'),
		setSearch: createSetter<SliceState>('searchValue'),
		toggleFilter(state, action: PayloadAction<State>) {
			const key = action.payload

			const index = state.enabledFilters.indexOf(key)
			if (index === -1) {
				state.enabledFilters.push(key)
			} else {
				state.enabledFilters.splice(index, 1)
			}
		}
	},
	selectors: {
		filterQuery: ({ enabledFilters }) => {
			const filtersArray = enabledFilters.map(
				filter => `(state eq '${filter}')`
			)
			return filtersArray.join(' or ')
		},

		filters: state => state.enabledFilters,
		isUpdatingTable: state => state.isUpdatingTable,
		scrollToAction: state => state.scrollToAction,
		search: state => state.searchValue
	}
})

const campaignsActions = campaignsSlice.actions
const campaignsSelectors = {
	...campaignsSlice.selectors,
	filtersAsSet: createSelector(
		campaignsSlice.selectors.filters,
		arr => new Set(arr)
	)
}

export { campaignsActions, campaignsSelectors, campaignsSlice }
